import { useEffect, useState } from 'react';
import { AlfaDocsService } from '../services/alfadocsService';
import { getCurrentUserOrganizationId } from '../stores/referenceDataCache';

// Studio AlfaDocs attivo dell'organizzazione (connessione api_configurations).
export interface AlfaDocsStudio {
  configId: string;   // id riga api_configurations
  practiceId: string; // configuration.practice_id
  name: string;       // display_name
}

// Cache a livello modulo per org (TTL sessione): più viste montano l'hook
// nella stessa navigazione senza rigenerare query. L'aggiunta/attivazione di
// una connessione avviene in Impostazioni: un refresh pagina è accettabile.
const studiosCache = new Map<string, AlfaDocsStudio[]>();
const pendingLoads = new Map<string, Promise<AlfaDocsStudio[]>>();

async function loadStudios(organizationId: string): Promise<AlfaDocsStudio[]> {
  const cached = studiosCache.get(organizationId);
  if (cached) return cached;
  const pending = pendingLoads.get(organizationId);
  if (pending) return pending;
  const load = AlfaDocsService.listConfigs(organizationId)
    .then((configs) => {
      const studios = configs
        .filter((c) => c.is_active && c.configuration?.practice_id)
        .map((c) => ({
          configId: c.id,
          practiceId: String(c.configuration!.practice_id),
          name: c.display_name || 'Studio',
        }));
      studiosCache.set(organizationId, studios);
      return studios;
    })
    .catch(() => [] as AlfaDocsStudio[])
    .finally(() => { pendingLoads.delete(organizationId); });
  pendingLoads.set(organizationId, load);
  return load;
}

// Gating unico per filtri/colonne "Studio AlfaDocs": la UI multi-studio
// compare SOLO quando isMultiStudio (>= 2 connessioni attive).
export function useAlfaDocsStudios(): {
  studios: AlfaDocsStudio[];
  isMultiStudio: boolean;
  oldestPracticeId: string | null;
  loading: boolean;
} {
  const [studios, setStudios] = useState<AlfaDocsStudio[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const organizationId = await getCurrentUserOrganizationId();
        const result = await loadStudios(organizationId);
        if (alive) setStudios(result);
      } catch {
        if (alive) setStudios([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return {
    studios,
    isMultiStudio: studios.length >= 2,
    oldestPracticeId: studios[0]?.practiceId ?? null,
    loading,
  };
}
